import { useState, useEffect } from 'react';
import { ShoppingCart, Package, ExternalLink } from 'lucide-react';
import { inventoryApi } from '../../services/inventoryApi';
import { getBcDocumentUrl } from '../../utils/bcLinks';
import type { OpenOrdersResponse } from '../../types/inventory';

interface OpenOrdersCardProps {
    itemId: string;
    companyCode: string;
}

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (d.getFullYear() < 1800) return '-';
    return d.toLocaleDateString('de-CH');
}

function formatNumber(num: number | null | undefined): string {
    if (num == null) return '-';
    return Math.round(num).toLocaleString('de-CH');
}

function formatCurrency(num: number | null | undefined): string {
    if (num == null) return '-';
    return new Intl.NumberFormat('de-CH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num);
}

export function OpenOrdersCard({ itemId, companyCode }: OpenOrdersCardProps) {
    const [data, setData] = useState<OpenOrdersResponse | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        inventoryApi.getOpenOrders(itemId)
            .then(setData)
            .catch(err => console.error('Failed to load open orders:', err))
            .finally(() => setLoading(false));
    }, [itemId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-4">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            </div>
        );
    }

    if (!data || (data.salesOrderCount === 0 && data.purchaseOrderCount === 0)) {
        return null;
    }

    // Shared column widths so both tables align vertically
    const colGroup = (
        <colgroup>
            <col style={{ width: '12%' }} />
            <col style={{ width: '28%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '14%' }} />
        </colgroup>
    );

    return (
        <div className="mt-4 space-y-4">
            {data.salesOrderCount > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3">
                    <h5 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-700">
                        <ShoppingCart className="h-3.5 w-3.5" />
                        Open Sales Orders ({data.salesOrderCount})
                    </h5>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm table-fixed">
                            {colGroup}
                            <thead>
                                <tr className="text-left text-xs text-gray-500">
                                    <th className="pb-1.5 pr-3 font-medium">Order</th>
                                    <th className="pb-1.5 pr-3 font-medium">Customer</th>
                                    <th className="pb-1.5 pr-3 text-right font-medium">Order Qty</th>
                                    <th className="pb-1.5 pr-3 text-right font-medium">Outstanding</th>
                                    <th className="pb-1.5 pr-3 text-right font-medium">Unit Price</th>
                                    <th className="pb-1.5 pr-3 text-right font-medium">Value EUR</th>
                                    <th className="pb-1.5 font-medium">Expected Delivery</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-amber-100">
                                {data.salesOrders.map((so, i) => {
                                    const unitPrice = so.OutstandingQuantity > 0
                                        ? so.OutstandingNetSalesEUR / so.OutstandingQuantity
                                        : null;
                                    return (
                                        <tr key={`${so.DocumentCode}-${i}`} className="text-gray-700">
                                            <td className="py-1 pr-3 font-mono text-xs truncate">{(() => {
                                            const url = getBcDocumentUrl(companyCode, so.DocumentCode, 'salesOrder');
                                            return url ? (
                                                <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                                                    {so.DocumentCode}<ExternalLink className="h-3 w-3" />
                                                </a>
                                            ) : so.DocumentCode;
                                        })()}</td>
                                            <td className="py-1 pr-3 truncate">{so.CustomerName || '-'}</td>
                                            <td className="py-1 pr-3 text-right font-mono">{formatNumber(so.OrderQuantity)}</td>
                                            <td className="py-1 pr-3 text-right font-mono">{formatNumber(so.OutstandingQuantity)}</td>
                                            <td className="py-1 pr-3 text-right font-mono text-gray-400">{unitPrice != null ? formatCurrency(unitPrice) : '-'}</td>
                                            <td className="py-1 pr-3 text-right font-mono">{formatCurrency(so.OutstandingNetSalesEUR)}</td>
                                            <td className="py-1">{formatDate(so.ExpectedDeliveryDate)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {data.purchaseOrderCount > 0 && (
                <div className="rounded-md border border-sky-200 bg-sky-50/50 p-3">
                    <h5 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-sky-700">
                        <Package className="h-3.5 w-3.5" />
                        Open Purchase Orders ({data.purchaseOrderCount})
                    </h5>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm table-fixed">
                            {colGroup}
                            <thead>
                                <tr className="text-left text-xs text-gray-500">
                                    <th className="pb-1.5 pr-3 font-medium">PO Nr.</th>
                                    <th className="pb-1.5 pr-3 font-medium">Vendor</th>
                                    <th className="pb-1.5 pr-3 text-right font-medium">Order Qty</th>
                                    <th className="pb-1.5 pr-3 text-right font-medium">Outstanding</th>
                                    <th className="pb-1.5 pr-3 text-right font-medium">Unit Cost</th>
                                    <th className="pb-1.5 pr-3 text-right font-medium">Amount EUR</th>
                                    <th className="pb-1.5 font-medium">Expected Receipt</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-sky-100">
                                {data.purchaseOrders.map((po, i) => (
                                    <tr key={`${po.PurchaseNr}-${i}`} className="text-gray-700">
                                        <td className="py-1 pr-3 font-mono text-xs truncate">{(() => {
                                            const url = getBcDocumentUrl(companyCode, po.PurchaseNr, 'purchaseOrder');
                                            return url ? (
                                                <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                                                    {po.PurchaseNr}<ExternalLink className="h-3 w-3" />
                                                </a>
                                            ) : po.PurchaseNr;
                                        })()}</td>
                                        <td className="py-1 pr-3 truncate">{po.VendorName || '-'}</td>
                                        <td className="py-1 pr-3 text-right font-mono">{formatNumber(po.Quantity)}</td>
                                        <td className="py-1 pr-3 text-right font-mono">{formatNumber(po.OutstandingQuantity)}</td>
                                        <td className="py-1 pr-3 text-right font-mono">{formatCurrency(po.DirectUnitCost)}</td>
                                        <td className="py-1 pr-3 text-right font-mono">{formatCurrency(po.AmountEUR)}</td>
                                        <td className="py-1">{formatDate(po.ExpectedReceiptDate)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
